import classNames from 'classnames';
import { DateTime } from 'luxon';
import * as React from 'react';
import {
	getFormValues,
	InjectedFormProps,
	reduxForm,
	SubmissionError,
} from 'redux-form';

import { TestingLocation } from '@aicpcu/web-api-spec-model';

import { AddCartButton } from '@app/components/add-cart-button';
import { PrimaryButton } from '@app/components/button';
import { PurchaseExamOverlay } from '@app/components/card-tab-overlay';
import { FormContainer, Select } from '@app/components/forms';
import {
	examTestingLocationRequired,
	examWindowRequired,
} from '@app/components/forms/validations.constants';
import { Loader } from '@app/components/loader';
import { Notification } from '@app/components/notification';
import { DOMESTIC_COUNTRY_VALUE } from '@app/constants/address';
import { FormValues } from '@app/interfaces/form';
import { Store } from '@app/interfaces/store/store';
import { webApi } from '@app/services/web-api';
import { gtmAddToCart } from '@app/utility/gtm';

import {
	PURCHASE_TAB_STRINGS,
	TabIdType,
	TestingLocations,
} from './purchase-form.constants';
import {
	ExamData,
	ExamDataOption,
	ExamDataStateKey,
	ExamExamWindowOptions,
	GroupedExamOptions,
	PropExamRadioGroup,
} from './purchase-form.types';
import {
	formUtil,
	localStorageUtil,
	redirectToLogin,
	tabUtil,
} from './purchase-form.util';
import {
	getCountryOptions,
	getLabel,
	getProgramOptions,
	getStateOptions,
	getTestingCenterOptions,
	getTestingLocationNote,
} from './purchase-tab-exam.util';
import {
	TestingLocationForm,
	TestingLocationFormComponent,
} from './testing-location-form';

type PropPurchaseTabExam = {
	actions: any;
	courseCode: string;
	description: string;
	examWindowOptions?: ExamExamWindowOptions[];
	hasNoExamWindows?: boolean;
	hidden: boolean;
	id: number;
	isSingleTab: boolean;
	note?: string;
	onSelect: () => void;
	setContainerHeight: (value: string, tabId: TabIdType) => void;
	store: Store;
};

const { EXAM_TAB } = PURCHASE_TAB_STRINGS;

/**
 * Login button. Sets local storage for a scroll journey after login redirect.
 *
 * @returns {React.ReactElement} Login button component.
 */
const LoginButton = ({
	courseCode,
	loginUrl,
}: {
	courseCode: string;
	loginUrl: string;
}): React.ReactElement => {
	const onLogin = () => {
		localStorageUtil.saveTab(EXAM_TAB.id, courseCode);
		redirectToLogin(loginUrl, `course-code-${courseCode}`);
	};
	return <PrimaryButton onClick={() => onLogin()}>Login</PrimaryButton>;
};

/**
 * Get exam type options by active exam window.
 *
 * @param {GroupedExamOptions} options Exam window options.
 *
 * @returns {Object} Selected exam options grouped by type.
 */
const splitExamOptions = (
	options?: GroupedExamOptions,
): PropExamRadioGroup => ({
	PROMETRIC: {
		label: 'Prometric',
		value: 'PROMETRIC',
		options: options?.PROMETRIC || [],
	},
	ONSITE: {
		label: 'On-site',
		value: 'ONSITE',
		options: options?.INTERNET || [],
	},
	VIRTUAL: {
		label: 'Virtual',
		value: 'VIRTUAL',
		options: options?.VIRTUAL || [],
	},
});

/**
 * Purchase Exam Form is Redux Form.
 *
 * Depending on an exam window selection, a radio group of exam location types
 * will be available. Upon successful form submission, an overlay is presented.
 *
 * Any parameter with "Redux or Redux Form" descriptor is connected and
 * composed.
 *
 * @param {object} actions              Redux Actions.
 * @param {number} courseCode           Parent course's code.
 * @param {object} examWindowOptions    Exam window options for select option.
 * @param {boolean} hasNoExamWindows        Has no exam windows. Defaults to first window.
 * @callback handleSubmit               Redux form handleSubmit callback.
 * @callback onSelect                   Handle overlay tab selection.
 * @callback reset                      Redux form reset function.
 *
 * @returns {React.Element} Purchase exam form component.
 */
const PurchaseTabExamRaw: React.FC<
	InjectedFormProps<{}, PropPurchaseTabExam> & PropPurchaseTabExam
> = ({
	actions: { setApiError, addToCart, addExamToCart, getCart },
	courseCode,
	description,
	examWindowOptions,
	hasNoExamWindows = false,
	hidden,
	id,
	isSingleTab,
	onSelect,
	setContainerHeight,
	store,
	note = '',
	...reduxFormProps
}: InjectedFormProps<{}, PropPurchaseTabExam> & PropPurchaseTabExam) => {
	const {
		change,
		form,
		handleSubmit,
		reset,
		submitFailed,
		submitSucceeded,
		submitting,
		valid,
	} = reduxFormProps;

	const panelId = tabUtil.setPanelId(EXAM_TAB.id, id);

	// Fetch form values.
	const formValues: FormValues = getFormValues(form)(store);

	const fieldNames = Object.freeze(formUtil.createExamFields(id));
	const {
		EXAMWINDOW_FIELDNAME,
		TESTING_LOCATION_FIELDNAME,
		TESTING_LOCATION_FIELDS,
	} = fieldNames;

	// Form value for: Exam Window.
	const selectedExamWindow = formUtil.selectValue(
		formValues,
		EXAMWINDOW_FIELDNAME,
	);

	// Form value for: Testing Location.
	const selectedTestingLocation = formUtil.selectValue(
		formValues,
		TESTING_LOCATION_FIELDNAME,
	);

	// Form value for: Exam.
	const selectedExam = TestingLocations.guard(selectedTestingLocation)
		? formUtil.selectValue(
				formValues,
				TESTING_LOCATION_FIELDS[selectedTestingLocation].EXAM_FIELDNAME,
		  )
		: '';

	// Determine exam options if a window is selected.
	const { options } =
		examWindowOptions?.find(({ value }) => value === selectedExamWindow) || {};
	const examOptions = splitExamOptions(options);

	// Find and deconstruct the selected exam option, if an exam option is selected.
	const selectedExamOption = TestingLocations.guard(selectedTestingLocation)
		? examOptions[selectedTestingLocation].options.find(
				({ value }) => value === selectedExam,
		  )
		: undefined;

	const {
		salePrice,
		label: selectedTitle = '',
		examDate = '',
		currentPrice,
		examWindowStartDate,
	} = selectedExamOption || { salePrice: 0, currentPrice: 0 };

	const examStartDate = examWindowStartDate
		? DateTime.fromFormat(examWindowStartDate, 'yyyy-MM-dd')
		: undefined;

	const shouldApplySalePrice = examStartDate
		? DateTime.now().toMillis() < examStartDate.toMillis()
		: false;

	const priceToDisplay = shouldApplySalePrice ? salePrice : currentPrice;

	// Determine if there are matching items and exam windows in cart.
	const hasMatchingCartItem = store.cart.cartItems.some(
		({ courseCode: cartCourseCode = '', inventoryClass = '' }) =>
			(inventoryClass === 'PK' && cartCourseCode === courseCode) ||
			(inventoryClass === 'SV' && !cartCourseCode),
	);

	const hasMatchingExamWindow = (() => {
		const matchingMaterial = store.cart.cartItems.find(
			({ courseCode: cartCourseCode = '', inventoryClass = '' }) =>
				inventoryClass === 'PK' && cartCourseCode === courseCode,
		);

		const { targetSeries = '' } = matchingMaterial || {};
		return (
			matchingMaterial &&
			selectedExamWindow &&
			targetSeries !== selectedExamWindow
		);
	})();

	// State for submit error.
	const [submitError, setSubmitError] = React.useState();

	const [storedTitle, setStoredTitle] = React.useState('');

	const [labels, setLabels] = React.useState({
		examWindow: '',
		locationType: '',
		exam: '',
		program: '',
		country: '',
		state: '',
		center: '',
	});

	const [locationOptions, setLocationOptions] = React.useState<
		readonly TestingLocation[]
	>([]);

	const initialExamDatum = { options: [], loading: false };
	const initialExamData = {
		centers: initialExamDatum,
		countries: initialExamDatum,
		programs: initialExamDatum,
		states: initialExamDatum,
	};

	const [examData, setExamData] = React.useState<ExamData>(initialExamData);
	const { centers, countries, programs, states } = examData;

	const showTestingLocationForm = Object.values(examOptions).some(
		({ options: opt }) => opt.length,
	);

	/**
	 * Resets Redux form fields defined by an array of fieldnames.
	 *
	 * @param {string[]} fields Redux form field names to reset.
	 */
	const resetFields = (...fields: (string | undefined)[]) =>
		fields.forEach((name) => name !== undefined && change(name, ''));

	/**
	 * Reset exam data to initial state for keys provided.
	 *
	 * If examDataKeys is empty, then reset the whole ExamData state.
	 *
	 * @param {ExamDataStateKey[]} examDataKeys Names of exam data keys to clear.
	 */
	const resetExamData = (...examDataKeys: ExamDataStateKey[]) =>
		examDataKeys.length > 0
			? setExamData({
					...examData,
					...examDataKeys.reduce(
						(clearedExamData: Partial<ExamData>, name) => ({
							...clearedExamData,
							[name]: initialExamDatum,
						}),
						{},
					),
			  })
			: setExamData(initialExamData);

	const setInitialExamWindow = React.useCallback(() => {
		if (examWindowOptions?.length) {
			change(EXAMWINDOW_FIELDNAME, examWindowOptions[0].value);
		}
	}, [change, EXAMWINDOW_FIELDNAME, examWindowOptions]);

	/**
	 * Completely reset the Redux Form and its calculated options.
	 */
	const resetForm = () => {
		resetExamData();
		reset();

		if (hasNoExamWindows) {
			setInitialExamWindow();
		}
	};

	const changeExamData = React.useCallback(
		(
			key: ExamDataStateKey,
			loading?: boolean,
			examDataOptions?: ExamDataOption[],
			show?: boolean,
			examDataNote?: string,
		) => {
			setExamData({
				...examData,
				[key]: {
					options:
						examDataOptions === undefined
							? examData[key].options
							: examDataOptions,
					loading: loading === undefined ? examData[key].loading : loading,
					show: show === undefined ? examData[key].show : show,
					note: examDataNote === undefined ? examData[key].note : examDataNote,
				},
			});
		},
		[examData],
	);

	/**
	 * Sets program state based on Course Code.
	 */
	const setProgramOptions = React.useCallback(async () => {
		changeExamData('programs', true);
		const programOptions = await getProgramOptions(courseCode, setApiError);
		changeExamData('programs', false, programOptions);
	}, [changeExamData, courseCode, setApiError]);

	/**
	 * Sets testing location and country options based on exam's ID.
	 *
	 * @param {string} examId Selected exam's ID.
	 */
	const setCountryOptions = async (examId: string) => {
		changeExamData('countries', true);
		try {
			const testingLocationData =
				await webApi.getTestingLocationsByContactAndLicense(Number(examId));
			setLocationOptions(testingLocationData);
			changeExamData(
				'countries',
				false,
				getCountryOptions(testingLocationData),
			);
		} catch (e) {
			setApiError(e);
		}
	};

	/**
	 * Sets state options based on location options.
	 */
	const setStateOptions = async () => {
		changeExamData('states', true, [], true);
		const statesOptions = await getStateOptions(locationOptions, setApiError);
		changeExamData('states', false, statesOptions, true);
	};

	/**
	 * Sets center options based on country and optional US state.
	 *
	 * @param {string} country Country code value.
	 * @param {string=} usSate  US State code value.
	 */
	const setCenterOptions = (country: string, usSate?: string) =>
		changeExamData(
			'centers',
			false,
			getTestingCenterOptions(locationOptions, country, usSate),
		);

	/**
	 * Set center note based on siteId selection.
	 *
	 * @param {string} siteId Site ID of testing center location.
	 */
	const setCenterNote = (siteId: string) =>
		changeExamData(
			'centers',
			false,
			undefined,
			undefined,
			getTestingLocationNote(centers.options, siteId),
		);

	/**
	 * Event handler to automate exam selection if exam has only one option.
	 *
	 * @event
	 */
	const setExamSelection: React.ChangeEventHandler<HTMLInputElement> = (
		event,
	) => {
		const { value } = event.target;
		if (!TestingLocations.guard(value)) {
			return;
		}

		const examOption = examOptions[value];
		const testingLocation = TESTING_LOCATION_FIELDS[value];

		if (examOption.options?.length === 1) {
			const examId = examOption.options[0].value;
			change(testingLocation.EXAM_FIELDNAME, examId);

			if (value === 'ONSITE' && !countries.options.length) {
				setCountryOptions(examId);
			}
		}
	};

	/**
	 * Event handler for Redux form item onChange.
	 *
	 * @event
	 */
	const handleOnChange: React.ChangeEventHandler<HTMLInputElement> = (
		event,
	) => {
		const { id: fieldName, value, name: group } = event.target;
		const { ONSITE } = TESTING_LOCATION_FIELDS;

		if (group === TESTING_LOCATION_FIELDNAME) {
			return setExamSelection(event);
		}

		switch (fieldName) {
			case EXAMWINDOW_FIELDNAME:
				resetForm();
				return setProgramOptions();

			case ONSITE.EXAM_FIELDNAME:
				resetExamData('countries', 'states', 'centers');
				resetFields(
					ONSITE.COUNTRY_FIELDNAME,
					ONSITE.STATE_FIELDNAME,
					ONSITE.CENTER_FIELDNAME,
					ONSITE.PROGRAM_FIELDNAME,
				);

				return setCountryOptions(value);

			case ONSITE.COUNTRY_FIELDNAME:
				resetExamData('states', 'centers');
				resetFields(
					ONSITE.STATE_FIELDNAME,
					ONSITE.CENTER_FIELDNAME,
					ONSITE.PROGRAM_FIELDNAME,
				);

				return value === DOMESTIC_COUNTRY_VALUE
					? setStateOptions()
					: setCenterOptions(value);

			case ONSITE.STATE_FIELDNAME:
				resetExamData('centers');
				resetFields(ONSITE.CENTER_FIELDNAME, ONSITE.PROGRAM_FIELDNAME);

				return setCenterOptions(DOMESTIC_COUNTRY_VALUE, value);

			case ONSITE.CENTER_FIELDNAME:
				resetFields(ONSITE.PROGRAM_FIELDNAME);
				return setCenterNote(value);

			default:
				return null;
		}
	};

	/**
	 * Handles redux form submission. Will only trigger if redux form is valid.
	 *
	 * @param {FormValues} values List of submitted form values.
	 */
	const handleSubmission = async (values: FormValues) => {
		const locationType = values[TESTING_LOCATION_FIELDNAME];

		if (!TestingLocations.guard(locationType)) {
			return;
		}

		const locationFields = TESTING_LOCATION_FIELDS[locationType];
		const {
			EXAM_FIELDNAME,
			PROGRAM_FIELDNAME,
			COUNTRY_FIELDNAME,
			STATE_FIELDNAME,
			CENTER_FIELDNAME,
		} = locationFields;

		const itemId = parseInt(values[EXAM_FIELDNAME], 10);
		const series = values[EXAMWINDOW_FIELDNAME];
		const centerCode = CENTER_FIELDNAME ? values[CENTER_FIELDNAME] : null;
		const programCode = values[PROGRAM_FIELDNAME];

		const registration = { centerCode, examDate, programCode };

		const { type, payload } =
			locationType === 'VIRTUAL'
				? await addToCart({
						itemId,
						series,
						...(programCode && { program: programCode }),
				  })
				: await addExamToCart({ itemId, series, registration });

		if (type === 'API_ERROR') {
			setSubmitError(payload);
			throw new SubmissionError({});
		}

		await gtmAddToCart(parseInt(values[EXAM_FIELDNAME], 10), programCode);

		await getCart('inline');

		setStoredTitle(selectedTitle);
		setLabels({
			examWindow: getLabel(
				values,
				EXAMWINDOW_FIELDNAME,
				examWindowOptions || [],
			),
			locationType: getLabel(
				values,
				TESTING_LOCATION_FIELDNAME,
				Object.values(examOptions),
			),
			exam: getLabel(values, EXAM_FIELDNAME, examOptions[locationType].options),
			program: getLabel(values, PROGRAM_FIELDNAME, programs.options),
			country: getLabel(values, COUNTRY_FIELDNAME, countries.options),
			state: getLabel(values, STATE_FIELDNAME, states.options),
			center: getLabel(values, CENTER_FIELDNAME, centers.options),
		});

		reset();
	};

	// Dispatch immediate exam window selection if No Window is set.
	React.useEffect(() => {
		if (hasNoExamWindows) {
			setInitialExamWindow();
			setProgramOptions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const COURSE_NOTE = note ? (
		<Notification className="u-margin-bottom">
			{/* eslint-disable-next-line react/no-danger */}
			<div dangerouslySetInnerHTML={{ __html: note }} />
		</Notification>
	) : null;

	/*
	 * Form container with an exam window Select, and a TestingLocationForm form component.
	 *
	 * Upon successful form submission, an PurchaseExamOverlay appears.
	 */
	return (
		<div
			className="purchase-tabs__panel"
			id={panelId}
			role={!isSingleTab ? 'tabpanel' : ''}
			aria-labelledby={!isSingleTab ? panelId : ''}
			hidden={hidden}
		>
			{store.auth.isAuthenticated ? (
				<>
					<p>{description}</p>
					<Notification className="u-margin-bottom">
						Designation exams now require virtual proctoring to further ensure exam integrity. <a href="/virtual-exam-proctoring" target="_blank">Learn more about how proctoring benefits you and what you will need.</a>
					</Notification>
					{COURSE_NOTE}
					<FormContainer
						isRedux
						className={classNames('purchase-tabs__purchase-form', {
							'has-loader': submitting,
						})}
						onSubmit={handleSubmit(handleSubmission)}
					>
						{submitting && <Loader isVeiled />}

						<ul className="u-list-unstyled">
							<li
								className={classNames('form__item', {
									'u-hidden': hasNoExamWindows,
								})}
							>
								<Select
									id={EXAMWINDOW_FIELDNAME}
									name={EXAMWINDOW_FIELDNAME}
									label="Select an Anticipated Exam Window"
									options={examWindowOptions}
									validate={[examWindowRequired]}
									tooltipMessage="We regularly update our content to provide you
            with the most up-to-date skills. Select your expected exam
            window to ensure you purchase the most current materials."
									onChange={handleOnChange}
								/>
							</li>

							<li className="form__item">
								{showTestingLocationForm && (
									<TestingLocationForm
										className="purchase-tabs__exams u-padding-bottom"
										examOptions={examOptions}
										examData={examData}
										formValues={formValues}
										handleOnChange={handleOnChange}
										id={TESTING_LOCATION_FIELDNAME}
										isSrOnly
										label="Select an Exam"
										name={TESTING_LOCATION_FIELDNAME}
										required
										testingLocationFields={TESTING_LOCATION_FIELDS}
										validate={[examTestingLocationRequired]}
										component={TestingLocationFormComponent}
									/>
								)}
							</li>
						</ul>

						{hasMatchingExamWindow && (
							<Notification className="u-margin-bottom">
								The window you selected to take your exam does not match the
								anticipated exam window of your materials.
							</Notification>
						)}

						<AddCartButton
							className={classNames({
								'btn--disabled': !valid,
							})}
							product={selectedTitle}
							price={priceToDisplay}
							error={submitFailed && submitError}
							isInForm
						/>
					</FormContainer>

					<PurchaseExamOverlay
						hasMatchingCartItem={hasMatchingCartItem}
						labels={labels}
						onClose={resetForm}
						onSelect={onSelect}
						show={submitSucceeded}
						title={storedTitle}
						isSingleForm={isSingleTab}
						setContainerHeight={setContainerHeight}
					/>
				</>
			) : (
				<>
					{COURSE_NOTE}
					<p className="u-margin-hug-top u-font-family-text">
						<strong>Sign in or create an account to purchase your exam.</strong>{' '}
						{description}
					</p>
					<LoginButton courseCode={courseCode} loginUrl={store.auth.loginUrl} />
				</>
			)}
		</div>
	);
};

export const PurchaseTabExam = reduxForm<{}, PropPurchaseTabExam>({})(
	PurchaseTabExamRaw,
);
